import React from 'react';

/**
 * @template T
 * @typedef {object} AsyncState
 * @property {boolean} loading
 * @property {Error | null} error
 * @property {T | null} data
 */

/**
 * @template T
 * @returns {AsyncState<T>}
 */
function initState() {
  return { loading: true, error: null, data: null };
}

/**
 * @template T
 * @param {(signal: AbortSignal) => Promise<T>} callback
 * @param {unknown[]} deps
 * @returns {AsyncState<T>}
 */
function useAsync(callback, deps) {
  /** @type {[AsyncState<T>, React.Dispatch<SetStateAction<AsyncState<T>>]} */
  const [state, setState] = React.useState(initState);

  React.useEffect(() => {
    setState((state) => ({ ...state, loading: true, error: null }));

    const controller = new AbortController();

    Promise.resolve(controller.signal)
      .then(callback)
      .then(
        (result) => {
          setState((state) => ({ ...state, loading: false, data: result }));
        },
        (error) => {
          setState({ loading: false, error, data: null });
        },
      );

    return () => {
      controller.abort();
    };
  }, deps);

  return state;
}

export default useAsync;
